import { useCallback } from "react";
import { trpc } from "lib/trpc";
import { ClientDocument } from "@compass/shared/types";

export interface UpdateDocumentHandlerOptions {
    id: string;
    filename: string;
    description?: string;
    file?: File;
}

export type UpdateDocumentHandler = (args: UpdateDocumentHandlerOptions) => Promise<ClientDocument>;

export const useUpdateDocument = (): UpdateDocumentHandler => {
    const queryClient = trpc.useContext();
    const startUpdate = trpc.userContent.update.start.useMutation();
    const completeUpdate = trpc.userContent.update.complete.useMutation();
    const updateMetadata = trpc.userContent.update.metadata.useMutation();

    return useCallback(
        async ({ id, filename, description, file }: UpdateDocumentHandlerOptions) => {
            if (!file) {
                // Metadata-only update
                const result = await updateMetadata.mutateAsync({ id, filename, description });

                await queryClient.userContent.latest.invalidate();
                await queryClient.userContent.list.invalidate();

                return result;
            }

            const contentType = file.type;

            const { uploadUrl } = await startUpdate.mutateAsync({
                id,
                filename,
                description,
                contentType,
            });

            const bufferFile = await file.arrayBuffer();

            const result = await fetch(uploadUrl, {
                method: "PUT",
                body: bufferFile,
            });

            if (result.status !== 200) {
                throw new Error("Failed to upload updated file");
            }

            const finalResult = await completeUpdate.mutateAsync({ id });

            await queryClient.userContent.latest.invalidate();
            await queryClient.userContent.list.invalidate();

            return finalResult;
        },
        [queryClient, startUpdate, completeUpdate, updateMetadata],
    );
};
